import LayoutPage from '@totem/components/devices/variables/presentation/layout/LayoutPage';
import DocumentsPage from '@totem/components/documents/report/documentsPage';
import EnergyPage from '@totem/components/energy/EnergyPage';
import PolicyAuditDetail from '@totem/components/policyAudits/policyAuditDetail/PolicyAuditDetail';
import PolicyAuditReport from '@totem/components/policyAudits/policyAuditDetail/Report';
import ReportPage from '@totem/components/reports/ReportsPage';
import Settings from '@totem/components/settings/Settings';
import ScoringPage from '@totem/components/surveyV2/instanceDetail/score/ScoringPage';
import SurveyDetailPage from '@totem/components/surveyV2/instanceDetail/SurveyDetailPage';
import WorkSummaryPage from '@totem/components/surveyV2/work/WorkSummaryPage';
import TicketDetailsPage from '@totem/components/ticketing/details/ticketDetailsPage';
import Tickets from '@totem/components/ticketing/list/tickets';
import MyTicketsPage from '@totem/components/ticketing/myTickets/myTicketsPage';
import { permissions } from '@totem/components/userContext/security/permissions/permissions';
import { securityGroupCheckConstraintAny } from '@totem/components/userContext/security/userUtilities';
import UserProfilePage from '@totem/components/userProfile/UserProfile';
import MyWorkPage from '@totem/components/users/myWork/MyWorkPage';
import { administrativeRoutes } from '@totem/routes/administrative';
import { applicationRoutes } from '@totem/routes/application';
import { contractRoutes } from '@totem/routes/contracts';
import { deviceRoutes } from '@totem/routes/devices';
import { eventRoutes } from '@totem/routes/events';
import { organizationRoutes } from '@totem/routes/organization';
import { portfolioRoutes } from '@totem/routes/portfolio';
import { remoteAccessRoutes } from '@totem/routes/remoteAccess';
import { softwareRoutes } from '@totem/routes/software';
import { surveyRoutes } from '@totem/routes/surveys';
import { ticketingRoutes } from '@totem/routes/ticketing';
import { userRoutes } from '@totem/routes/users';
import { vendorManagementRoutes } from '@totem/routes/vendorManagement';
import { Route } from '@totem/types/route';
import { UserProfile } from '@totem/types/user';

const baseRoutes: Route[] = [
  {
    path: 'documents',
    pageTitle: 'Document Repository',
    component: DocumentsPage,
    exact: true,
    breadcrumbs: [],
    render: (userProfile: UserProfile) =>
      permissions.collaborator.hasPermissions(
        userProfile,
        securityGroupCheckConstraintAny,
      ),
  },
  {
    path: 'energy',
    pageTitle: 'Energy Overview',
    component: EnergyPage,
    exact: true,
    breadcrumbs: [{ label: 'Energy Overview' }],
    render: (userProfile: UserProfile) =>
      permissions.collaborator.hasPermissions(
        userProfile,
        securityGroupCheckConstraintAny,
      ),
  },
  {
    path: 'myWork',
    pageTitle: 'My Work',
    component: MyWorkPage, // WorkSummary, WorkSummaryPage
    exact: true,
    breadcrumbs: [],
  },
];

export const getBaseRoutes = (user: UserProfile) => {
  const allRoutes = [
    ...baseRoutes,
    ...portfolioRoutes,
    ...organizationRoutes,
    ...ticketingRoutes,
    ...deviceRoutes,
    ...surveyRoutes,
    ...eventRoutes,
    ...remoteAccessRoutes,
    ...contractRoutes,
    ...administrativeRoutes,
    ...softwareRoutes,
    ...userRoutes,
    ...vendorManagementRoutes,
    ...applicationRoutes,
  ];

  return allRoutes.map((route) => ({
    ...route,
    /**
     * all base routes prefixed with /dashboard and organization breadcrumb
     * all breadcrumb links prefixed with /dashboard
     */
    path: route.path,
    breadcrumbs: [
      {
        link: '/dashboard',
        label: user.organization?.name || 'IB',
      },
      ...route.breadcrumbs.map((breadcrumb) => ({
        ...breadcrumb,
        link: breadcrumb.link ? `/dashboard${breadcrumb.link}` : '',
      })),
    ],
  }));
};

const vendorRoutes: Route[] = [
  {
    path: '',
    pageTitle: 'Your Work',
    component: WorkSummaryPage, // WorkSummary
    exact: true,
    breadcrumbs: [],
  },
  {
    path: 'surveyinstances/:id',
    pageTitle: 'Survey Detail',
    component: SurveyDetailPage,
    exact: true,
    breadcrumbs: [],
  },
  {
    path: 'surveyinstances/:id/score',
    pageTitle: 'Survey Results',
    component: ScoringPage,
    exact: true,
    breadcrumbs: [],
  },
  {
    path: 'policyaudits/:id',
    pageTitle: 'Policy Audit Detail',
    component: PolicyAuditDetail,
    exact: true,
    breadcrumbs: [],
  },
  {
    path: 'policyaudits/:id/report',
    pageTitle: 'Audit Report',
    component: PolicyAuditReport,
    exact: true,
    breadcrumbs: [],
  },
  {
    path: 'userProfile',
    pageTitle: 'User Profile',
    component: UserProfilePage,
    exact: true,
    breadcrumbs: [{ label: 'User Profile' }],
  },
  {
    path: 'settings',
    pageTitle: 'Settings',
    component: Settings,
    exact: true,
    breadcrumbs: [{ label: 'Settings' }],
  },
  {
    path: 'ticketing',
    pageTitle: 'Tickets',
    component: Tickets,
    exact: true,
    breadcrumbs: [{ label: 'Tickets' }],
  },
  {
    path: 'ticket/:id',
    pageTitle: 'Tickets',
    component: TicketDetailsPage,
    exact: true,
    breadcrumbs: [{ label: 'Ticket' }],
  },
  {
    path: 'myTickets',
    pageTitle: 'My Tickets',
    component: MyTicketsPage,
    exact: true,
    breadcrumbs: [{ label: 'My Tickets' }],
  },
];

export const getVendorRoutes = (user: UserProfile) =>
  vendorRoutes.map((route) => ({
    ...route,
    /**
     * all base routes prefixed with /vendor-dashboard and organization breadcrumb
     * all breadcrumb links prefixed with /vendor-dashboard
     */
    path: route.path,
    breadcrumbs: [
      {
        link: '/vendor-dashboard',
        label: user.organization?.name || 'IB',
      },
      ...route.breadcrumbs.map((breadcrumb) => ({
        ...breadcrumb,
        link: breadcrumb.link ? `/vendor-dashboard${breadcrumb.link}` : '',
      })),
    ],
  }));

const tenantRoutes: Route[] = [
  {
    path: '',
    pageTitle: 'Reports',
    component: ReportPage,
    exact: true,
    breadcrumbs: [],
  },
  {
    path: 'reports',
    pageTitle: 'Reports',
    component: ReportPage,
    exact: true,
    breadcrumbs: [],
  },
  {
    path: 'userProfile',
    pageTitle: 'User Profile',
    component: UserProfilePage,
    exact: true,
    breadcrumbs: [{ label: 'User Profile' }],
  },
  {
    path: 'variable/layout/:id',
    pageTitle: 'Variables',
    component: LayoutPage,
    exact: true,
    breadcrumbs: [],
  },
];

export const getTenantRoutes = (user: UserProfile) =>
  tenantRoutes.map((route) => ({
    ...route,
    /**
     * all base routes prefixed with /vendor-dashboard and organization breadcrumb
     * all breadcrumb links prefixed with /vendor-dashboard
     */
    path: route.path,
    breadcrumbs: [
      {
        link: '/tenant-dashboard',
        label: user.organization?.name || 'IB',
      },
      ...route.breadcrumbs.map((breadcrumb) => ({
        ...breadcrumb,
        link: breadcrumb.link ? `/tenant-dashboard${breadcrumb.link}` : '',
      })),
    ],
  }));
